/**
 * Leaderboard View
 *
 * Displays top players using real API data.
 * Uses DopeUI for consistent styling.
 */

import { registerView } from '../viewRegistry';
import { getCanonicalSafeArea } from '../safeArea';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getLeaderboard, getPlayerStats, getAuthWallet } from '../../utils/api';
import DopeUI, { TERMINAL, SPACING } from '../ui/DopeOS-UI';

// State for live data
let leaderboardState = {
  data: [],
  loading: true,
  error: null,
  lastFetch: 0,
  playerRank: null,
};

async function loadLeaderboardData() {
  // Cache for 30 seconds
  if (Date.now() - leaderboardState.lastFetch < 30000 && leaderboardState.data.length > 0) {
    return;
  }

  leaderboardState.loading = true;
  leaderboardState.error = null;

  try {
    // Fetch leaderboard data
    const response = await getLeaderboard({ limit: 10 });

    if (response && response.players) {
      leaderboardState.data = response.players.map((player, idx) => ({
        rank: idx + 1,
        name: player.display_name || player.username || player.wallet_address?.slice(0, 12) || `BOSS_${idx + 1}`,
        score: player.overall_score ?? player.score ?? player.total_score ?? 0,
        rep: player.reputation_tier || getRepTier(player.overall_score ?? player.score ?? 0),
        wallet: player.wallet_address || player.wallet,
      }));
    } else if (Array.isArray(response)) {
      leaderboardState.data = response.map((player, idx) => ({
        rank: idx + 1,
        name: player.display_name || player.username || player.wallet_address?.slice(0, 12) || `BOSS_${idx + 1}`,
        score: player.overall_score ?? player.score ?? player.total_score ?? 0,
        rep: player.reputation_tier || getRepTier(player.overall_score ?? player.score ?? 0),
        wallet: player.wallet_address || player.wallet,
      }));
    }

    // Fetch current player's stats
    const wallet = getAuthWallet();
    if (wallet) {
      try {
        const playerStats = await getPlayerStats(wallet);
        leaderboardState.playerRank = {
          rank: playerStats.rank || '??',
          score: playerStats.total_score || playerStats.score || 0,
          rep: playerStats.reputation_tier || getRepTier(playerStats.total_score || 0),
        };
      } catch (e) {
        console.warn('[Leaderboard] Could not fetch player stats:', e);
      }
    }

    leaderboardState.lastFetch = Date.now();
  } catch (error) {
    console.error('[Leaderboard] Failed to load:', error);
    leaderboardState.error = 'Failed to load rankings';
    // Fallback to empty state - no mock data
    leaderboardState.data = [];
  } finally {
    leaderboardState.loading = false;
  }
}

function getRepTier(score) {
  if (score >= 2000000) return 'BOSS';
  if (score >= 1000000) return 'CAPO';
  if (score >= 500000) return 'SOLDIER';
  return 'ASSOCIATE';
}

function renderLeaderboard(ctx, time, w, h, params = {}) {
  // Clear interactions at start of frame
  interactiveRegistry.clear();

  // Clear background
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, w, h);

  const safe = getCanonicalSafeArea({ w, h });
  const scale = 1.1;

  // Load data on first render
  if (leaderboardState.loading && leaderboardState.data.length === 0) {
    loadLeaderboardData();
  }

  // Header panel
  const headerHeight = 50;
  DopeUI.Panel(ctx, safe.left, safe.top, safe.width, headerHeight, 'LEADERBOARD');

  // Close button
  const closeBtnX = safe.right - 70;
  const closeBtnY = safe.top + 10;
  DopeUI.Button(ctx, closeBtnX, closeBtnY, 60, 28, 'CLOSE', 'normal');

  interactiveRegistry.register({
    id: 'leaderboard-close',
    x: closeBtnX,
    y: closeBtnY,
    w: 60,
    h: 28,
    onClick: () => {
      navigateTo('main_menu');
    }
  });

  // Content area
  const contentY = safe.top + headerHeight + SPACING.SM;
  const contentH = safe.height - headerHeight - SPACING.MD;
  const isMobileLB = safe.width < 400;
  const rowHeight = isMobileLB ? 40 : 35;

  // Loading state
  if (leaderboardState.loading) {
    DopeUI.Spinner(ctx, safe.left + safe.width / 2, contentY + contentH / 2, 30, time);
    DopeUI.Text(ctx, 'LOADING RANKINGS...', safe.left + safe.width / 2 - 60, contentY + contentH / 2 + 40, 'dim', 1.0);
    return;
  }

  // Error state
  if (leaderboardState.error) {
    DopeUI.Text(ctx, 'CONNECTION LOST', safe.left + safe.width / 2 - 50, contentY + 50, 'dim', 1.2);
    DopeUI.Text(ctx, leaderboardState.error, safe.left + SPACING.MD, contentY + 80, 'dim', 0.9);

    // Retry button
    const retryX = safe.left + safe.width / 2 - 40;
    const retryY = contentY + 120;
    DopeUI.Button(ctx, retryX, retryY, 80, 28, 'RETRY', 'normal');
    interactiveRegistry.register({
      id: 'leaderboard-retry',
      x: retryX,
      y: retryY,
      w: 80,
      h: 28,
      onClick: () => {
        leaderboardState.lastFetch = 0;
        loadLeaderboardData();
      }
    });
    return;
  }

  // Empty state
  if (leaderboardState.data.length === 0) {
    DopeUI.Text(ctx, 'NO RANKINGS YET', safe.left + safe.width / 2 - 50, contentY + 100, 'dim', 1.2);
    DopeUI.Text(ctx, 'Be the first to make the leaderboard!', safe.left + SPACING.MD, contentY + 130, 'dim', 0.9);
    return;
  }

  // Table header - responsive column positions
  const colX = {
    rank: safe.left + SPACING.MD,
    name: safe.left + (isMobileLB ? 45 : 60),
    score: safe.left + safe.width - (isMobileLB ? 130 : 180),
    rep: safe.left + safe.width - (isMobileLB ? 55 : 80),
  };

  // Header row
  ctx.fillStyle = TERMINAL.GREEN_DARK;
  ctx.fillRect(safe.left, contentY, safe.width, rowHeight);
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.lineWidth = 1;
  ctx.strokeRect(safe.left, contentY, safe.width, rowHeight);

  DopeUI.Text(ctx, '#', colX.rank, contentY + 10, 'bright', scale);
  DopeUI.Text(ctx, 'BOSS', colX.name, contentY + 10, 'bright', scale);
  DopeUI.Text(ctx, 'CASH', colX.score, contentY + 10, 'bright', scale);
  DopeUI.Text(ctx, 'REP', colX.rep, contentY + 10, 'bright', scale);

  // Data rows
  leaderboardState.data.forEach((player, idx) => {
    const rowY = contentY + rowHeight + 4 + idx * (rowHeight + 4);

    // Row background (alternating)
    ctx.fillStyle = idx % 2 === 0 ? TERMINAL.GREEN_DARKEST : TERMINAL.BLACK;
    ctx.fillRect(safe.left + SPACING.SM, rowY, safe.width - SPACING.MD, rowHeight);

    // Rank color based on position
    const rankColor = player.rank === 1 ? 'bright' : player.rank === 2 ? 'normal' : player.rank === 3 ? 'dim' : 'dim';
    DopeUI.Text(ctx, `${player.rank}`, colX.rank, rowY + 10, rankColor, scale);

    // Name - truncate on mobile if too long
    let displayName = player.name;
    if (isMobileLB && displayName.length > 12) {
      displayName = displayName.slice(0, 10) + '..';
    }
    DopeUI.Text(ctx, displayName, colX.name, rowY + 10, 'normal', isMobileLB ? 0.9 : scale);

    // Score
    const scoreStr = isMobileLB
      ? `${(player.score / 1000).toFixed(0)}K`
      : `$${player.score.toLocaleString()}`;
    DopeUI.Text(ctx, scoreStr, colX.score, rowY + 10, 'normal', isMobileLB ? 0.85 : scale);

    // Reputation badge
    const repColor = player.rep === 'BOSS' ? 'bright' : player.rep === 'CAPO' ? 'normal' : 'dim';
    const displayRep = isMobileLB && player.rep === 'ASSOCIATE' ? 'ASSOC' : player.rep;
    DopeUI.Text(ctx, displayRep, colX.rep, rowY + 10, repColor, isMobileLB ? 0.8 : 0.9);
  });

  // Player's own rank (if not in top 10)
  if (leaderboardState.playerRank && leaderboardState.playerRank.rank > 10) {
    const playerRowY = contentY + rowHeight + 4 + leaderboardState.data.length * (rowHeight + 4) + 20;

    ctx.fillStyle = TERMINAL.GREEN_DARK;
    ctx.fillRect(safe.left + SPACING.SM, playerRowY, safe.width - SPACING.MD, rowHeight);
    ctx.strokeStyle = TERMINAL.TEXT;
    ctx.lineWidth = 1;
    ctx.strokeRect(safe.left + SPACING.SM, playerRowY, safe.width - SPACING.MD, rowHeight);

    DopeUI.Text(ctx, `${leaderboardState.playerRank.rank}`, colX.rank, playerRowY + 10, 'bright', scale);
    DopeUI.Text(ctx, 'YOU', colX.name, playerRowY + 10, 'bright', scale);
    const scoreStr = isMobileLB
      ? `${(leaderboardState.playerRank.score / 1000).toFixed(0)}K`
      : `$${leaderboardState.playerRank.score.toLocaleString()}`;
    DopeUI.Text(ctx, scoreStr, colX.score, playerRowY + 10, 'normal', isMobileLB ? 0.85 : scale);
    const rep = leaderboardState.playerRank.rep;
    const displayRep = isMobileLB && rep === 'ASSOCIATE' ? 'ASSOC' : rep;
    DopeUI.Text(ctx, displayRep, colX.rep, playerRowY + 10, 'normal', isMobileLB ? 0.8 : 0.9);
  }

  // Footer info
  const footerY = safe.bottom - 20;
  DopeUI.Text(ctx, `Top ${leaderboardState.data.length} bosses this week`, safe.left + SPACING.MD, footerY, 'dim', 0.9);

  // Refresh button
  const refreshX = safe.right - 70;
  const refreshY = footerY - 5;
  DopeUI.Text(ctx, '[REFRESH]', refreshX, refreshY, 'dim', 0.8);
  interactiveRegistry.register({
    id: 'leaderboard-refresh',
    x: refreshX,
    y: refreshY - 10,
    w: 60,
    h: 20,
    onClick: () => {
      leaderboardState.lastFetch = 0;
      loadLeaderboardData();
    }
  });
}

registerView('leaderboard', renderLeaderboard);

export default 'leaderboard';
